import React from "react";
import { Tooltip } from "@fluentui/react-components";
import { getLabelByType, getProjectStatusPillClass } from "../../helpers/utils";
import TruncatedText from "../truncatedText/TruncatedText";
import styles from "./resourceInsights.module.scss";
import { formatDateRange, formatHours } from "./cardFormat";

/** A count of tasks that opens those tasks when the user may see tasks, and is plain text when they may not. */
function TaskCount({ children, canOpenTasks, onClick }) {
  if (!canOpenTasks) return <span>{children}</span>;

  return (
    <button type="button" className={styles.countLink} onClick={onClick}>
      {children}
    </button>
  );
}

/**
 * A name that opens the tasks it stands for, with a tooltip saying so, when the user may see tasks; otherwise just the name.
 *
 * @param label  what the name is called in the tooltip, as in "Filter by Contoso"
 */
function FilterLink({ children, label, canOpenTasks, onClick, className }) {
  if (!canOpenTasks) return <span className={className}>{children}</span>;

  return (
    <Tooltip content={`Filter by ${label}`} relationship="description" positioning="above-start">
      <button type="button" className={`${styles.filterLink} ${className || ""}`} onClick={onClick}>
        {children}
      </button>
    </Tooltip>
  );
}

/** A client at the top of the tab, which opens the tasks of its projects when the user may see tasks. */
function ClientChip({ client, canOpenTasks, onOpenClient }) {
  const content = (
    <>
      {client.image && <img src={client.image} alt="" className={styles.clientLogo} />}
      {client.name}
      <em>{client.projectCount}</em>
    </>
  );

  if (!canOpenTasks) return <span className={styles.clientChip}>{content}</span>;

  return (
    <Tooltip content={`Filter by ${client.name}`} relationship="description" positioning="above-start">
      <button type="button" className={`${styles.clientChip} ${styles.clientChipButton}`} onClick={() => onOpenClient(client.id)}>
        {content}
      </button>
    </Tooltip>
  );
}

/**
 * The projects the resource is on, with the clients they belong to. The number of active, completed and critical
 * tasks of a project open the tasks of that project, and so do its name (all its tasks) and the name of its client,
 * on the project and among the clients at the top (the tasks of all that client's projects). The hours on validated timesheets in the period are shown next to the
 * counts while hours are shown.
 *
 * @param onOpenTasks  `(count, projectId)`, asks for the list of the tasks behind a count of one project
 * @param onOpenProject  `(projectId)`, asks for the tasks of one project
 * @param onOpenClient  `(clientId)`, asks for the tasks of the projects of one client
 */
export default function ProjectsTab({ card, showHours, canOpenTasks, onOpenTasks, onOpenProject, onOpenClient }) {
  const { clients, projects } = card;

  if (projects.length === 0) {
    return <p className={styles.empty}>This resource is not assigned to any project you can access.</p>;
  }

  return (
    <div className={styles.tabContent}>
      {clients.length > 0 && (
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h4>Clients</h4>
          </div>
          <div className={styles.chipList}>
            {clients.map((client) => (
              <ClientChip key={client.id} client={client} canOpenTasks={canOpenTasks} onOpenClient={onOpenClient} />
            ))}
          </div>
        </section>
      )}

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h4>Projects</h4>
          {card.period && (
            <span className={styles.sectionHint}>
              {showHours ? "Tasks and validated hrs of" : "Tasks of"} {formatDateRange(card.period.startDate, card.period.endDate)}
            </span>
          )}
        </div>
        <div className={styles.cardList}>
          {projects.map((project) => (
            <div key={project.id} className={styles.projectCard}>
              <div className={styles.projectCardHeader}>
                <div className={styles.projectName}>
                  {canOpenTasks ? (
                    <FilterLink canOpenTasks label={project.name} className={styles.projectNameLink} onClick={() => onOpenProject(project.id)}>
                      {project.name}
                    </FilterLink>
                  ) : (
                    <TruncatedText text={project.name} />
                  )}
                  {project.refId && <em>{project.refId}</em>}
                </div>
                <span className={getProjectStatusPillClass(project.status)}>{getLabelByType("project", project.status)}</span>
              </div>

              <div className={styles.progressRow}>
                <div className={styles.barTrack}>
                  <div className={styles.barFill} style={{ width: `${project.progress}%` }} />
                </div>
                <span>{project.progress}%</span>
              </div>

              <div className={styles.projectMeta}>
                {project.client?.id ? (
                  <FilterLink canOpenTasks={canOpenTasks} label={project.client.name} className={styles.clientNameLink} onClick={() => onOpenClient(project.client.id)}>
                    {project.client.name}
                  </FilterLink>
                ) : (
                  <span>{project.client?.name}</span>
                )}
                <span>{formatDateRange(project.startDate, project.endDate)}</span>
              </div>

              <div className={styles.projectMeta}>
                {showHours && <span>Committed {formatHours(project.weeklyShareHours)} / week</span>}
                {project.activeTasks != null && (
                  <span className={styles.counts}>
                    <TaskCount canOpenTasks={canOpenTasks} onClick={() => onOpenTasks("active", project.id)}>
                      {project.activeTasks} active
                    </TaskCount>
                    <TaskCount canOpenTasks={canOpenTasks} onClick={() => onOpenTasks("completed", project.id)}>
                      {project.completedTasks} completed
                    </TaskCount>
                    {project.criticalTasks > 0 && (
                      <TaskCount canOpenTasks={canOpenTasks} onClick={() => onOpenTasks("critical", project.id)}>
                        {project.criticalTasks} critical
                      </TaskCount>
                    )}
                    {showHours && project.validatedHours != null && (
                      <Tooltip content="Actual hrs, which are the hours on validated timesheets: this resource on this project, on the days of the period" relationship="description" positioning="above">
                        <span className={styles.validatedHours}>{formatHours(project.validatedHours)} validated</span>
                      </Tooltip>
                    )}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
